import { Button, Descriptions, Dropdown, MenuProps, Modal, Space, Spin, Typography } from 'antd';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import { IGlobalState } from '~reducer';
import ModalStateEnum from '~UI/ModalStateEnum';
import DrawdownQsInstructedModal from './DrawdownQsInstructedModal';
import LoanDrawdownStatusEnum from '~Api/Loan/LoanDrawdownStatusEnum';
import { useParams } from 'react-router-dom';
import {
    loanDrawdownApproveAction,
    loanDrawdownGetAction,
    loanDrawdownRejectAction,
    loanDrawdownSubmitForApprovalAction,
    loanGetAction,
    loanWarehouseLoansListAction,
} from '~Loans/actions';
import {
    loanDrawdownItemsSelector,
    loanDrawdownSelector,
    loanSelector,
    loanWarehouseLoansSelector,
} from '~Loans/selectors';
import ILoan from '~Api/Loan/ILoan';
import Layout from '~/Loans/Loan/Layout';
import IAuthUser from '~Auth/IAuthUser';
import { authCurrentUserSelector } from '~Auth/selectors';
import PermissionsEnum from '~Api/Administrator/PermissionsEnum';
import DrawdownQsReceivedModal from './DrawdownQsReceivedModal';
import DrawdownDisbursalInstructedModal from './DrawdownDisbursalInstructedModal';
import DrawdownDisbursalCalculationModal from './DrawdownDisbursalCalculationModal';
import { IDictionary } from '~utilities/IDictionary';
import dayjs from 'dayjs';
import ILoanDrawdownItem from '~Api/Loan/ILoanDrawdownItem';
import _ from 'lodash';
import { currencyFormatter } from '~utilities/formatters';
import { getLoanDrawdownTotalToBeDisbursed } from '~Loans/utilities';
import PopoverWarning from '~UI/PopoverWarning';
import DrawdownProcessModal from './DrawdownProcessModal';
import IWarehouse from '~Api/Warehouse/IWarehouse';
import { warehousesSelector } from '~Warehouses/selectors';
import { warehousesListAction } from '~Warehouses/actions';
import WarehouseTypeEnum from '~Api/Warehouse/WarehouseTypeEnum';
import IWarehouseLoan from '~Api/Warehouse/IWarehouseLoan';
import LoanDrawdownStatusTag from '../LoanDrawdownStatusTag';

const validateAndDisplayBalanceStatuses: LoanDrawdownStatusEnum[] = [
    LoanDrawdownStatusEnum.QsReceived,
    LoanDrawdownStatusEnum.PendingApproval,
    LoanDrawdownStatusEnum.Approved,
    LoanDrawdownStatusEnum.DisbursalInstructed,
];

interface ITableErrors {
    approvedDrawdownAmount?: string;
    retainedContingencyAmount?: string;
    retainedFundsAmount?: string;
    totalToBeDisbursed?: string;
}

interface IItemErrors {
    amount?: string;
    description?: string;
}

export default function Drawdown(): ReactElement {
    const { loanDrawdownUuid, loanUuid } = useParams<{ loanUuid: string; loanDrawdownUuid: string }>();

    const currentUser: IAuthUser = useSelector(authCurrentUserSelector);
    const drawdown: ILoanDrawdown = useSelector((state: IGlobalState) => loanDrawdownSelector(state, loanDrawdownUuid));
    const loan: ILoan = useSelector((state: IGlobalState) => loanSelector(state, loanUuid));
    const items: IDictionary<ILoanDrawdownItem> = useSelector((state: IGlobalState) => loanDrawdownItemsSelector(state, loanDrawdownUuid));
    const warehouses: IDictionary<IWarehouse> = useSelector(warehousesSelector);
    const loanWarehouseLoans: IDictionary<IWarehouseLoan> = useSelector((state: IGlobalState) => loanWarehouseLoansSelector(state, loanUuid));

    const [ isDisbursalInstructedModalOpen, setIsDisbursalInstructedModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ isQsInstructedModalOpen, setIsQsInstructedModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ isQsReceivedModalOpen, setIsQsReceivedModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ isDisbursalCalculationModalOpen, setIsDisbursalCalculationModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ isProcessModalOpen, setIsProcessModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!drawdown || !items) {
            dispatch(loanDrawdownGetAction(loanDrawdownUuid));
        }
    }, [
        dispatch,
        drawdown,
        items,
        loanDrawdownUuid,
    ]);

    useEffect(() => {
        if (!loan) {
            dispatch(loanGetAction(loanUuid));
        }
    }, [
        dispatch,
        loan,
        loanUuid,
    ]);

    useEffect(() => {
        if (!warehouses && currentUser.permissions.includes(PermissionsEnum.Warehouses)) {
            dispatch(warehousesListAction());
        }
    }, [
        dispatch,
        warehouses,
    ]);

    useEffect(() => {
        if (!loanWarehouseLoans && currentUser.permissions.includes(PermissionsEnum.Warehouses)) {
            dispatch(loanWarehouseLoansListAction(loanUuid));
        }
    }, [
        dispatch,
        loanWarehouseLoans,
    ]);

    const onClickApprove: () => void = useCallback(() => {
        Modal.confirm({
            content: 'Are you sure you want to approve this drawdown?',
            okText: 'Approve',
            onOk: () => {
                dispatch(loanDrawdownApproveAction(loanDrawdownUuid));
            },
            title: 'Approve',
        });
    }, [
        dispatch,
        loanDrawdownUuid,
    ]);

    const onClickReject: () => void = useCallback(() => {
        Modal.confirm({
            content: 'Are you sure you want to reject this drawdown?',
            okText: 'Reject',
            onOk: () => {
                dispatch(loanDrawdownRejectAction(loanDrawdownUuid));
            },
            title: 'Reject',
        });
    }, [
        dispatch,
        loanDrawdownUuid,
    ]);

    const onClickDisbursalCalculation: () => void = useCallback(() => {
        setIsDisbursalCalculationModalOpen(ModalStateEnum.Open);
    }, []);

    const onCloseDisbursalCalculationModal: () => void = useCallback(() => {
        setIsDisbursalCalculationModalOpen(ModalStateEnum.Closed);
    }, []);

    const onClickInstructDisbursal: () => void = useCallback(() => {
        setIsDisbursalInstructedModalOpen(ModalStateEnum.Open);
    }, []);

    const onCloseDisbursalInstructedModal: () => void = useCallback(() => {
        setIsDisbursalInstructedModalOpen(ModalStateEnum.Closed);
    }, []);

    const onClickProcess: () => void = useCallback(() => {
        setIsProcessModalOpen(ModalStateEnum.Open);
    }, []);

    const onCloseProcessModal: () => void = useCallback(() => {
        setIsProcessModalOpen(ModalStateEnum.Closed);
    }, []);

    const onClickQsInstructed: () => void = useCallback(() => {
        setIsQsInstructedModalOpen(ModalStateEnum.Open);
    }, []);

    const onCloseQsInstructedModal: () => void = useCallback(() => {
        setIsQsInstructedModalOpen(ModalStateEnum.Closed);
    }, []);

    const onClickQsReceived: () => void = useCallback(() => {
        setIsQsReceivedModalOpen(ModalStateEnum.Open);
    }, []);

    const onCloseQsReceivedModal: () => void = useCallback(() => {
        setIsQsReceivedModalOpen(ModalStateEnum.Closed);
    }, []);

    const onClickSubmitForApproval: () => void = useCallback(() => {
        dispatch(loanDrawdownSubmitForApprovalAction(loanDrawdownUuid));
    }, [
        dispatch,
        loanDrawdownUuid,
    ]);

    if (!drawdown || !loan) {
        return (
            <Layout uuid={loanUuid} section='drawdown'>
                <Spin/>
            </Layout>
        );
    }

    const actionsMenu: MenuProps = {
        items: [
            {
                danger: true,
                key: 'reject',
                label: 'Reject',
                onClick: onClickReject,
            },
        ],
    };

    const approveActionBlock: JSX.Element = currentUser.permissions.includes(PermissionsEnum.LoanDrawdownApprove) && drawdown.status === LoanDrawdownStatusEnum.PendingApproval && (
        <Dropdown.Button type='primary' onClick={onClickApprove} menu={actionsMenu}>Approve</Dropdown.Button>
    );

    const processActionBlock: ReactElement = currentUser.permissions.includes(PermissionsEnum.Accounts) && drawdown.status === LoanDrawdownStatusEnum.DisbursalInstructed && (
        <Button type='primary' onClick={onClickProcess}>Process</Button>
    );

    let loanHasFwt1Warehouse: boolean = false;

    if (warehouses && loanWarehouseLoans) {
        _.forEach(loanWarehouseLoans, (loanWarehouseLoan: IWarehouseLoan) => {
            if (WarehouseTypeEnum.Fwt1 === warehouses[loanWarehouseLoan.warehouseUuid].type && loanWarehouseLoan.balancePrincipal <= 0) {
                loanHasFwt1Warehouse = true;
            }
        });
    }

    const disbursalInstructedMenu: MenuProps = !!loanHasFwt1Warehouse && drawdown.status === LoanDrawdownStatusEnum.Approved && {
        items: [
            {
                key: 'download-offer-to-sell',
                label: <a href={`${process.env.API_HOST}/loan-drawdowns/${drawdown.uuid}/download-offer-to-sell`} target='_blank' rel='noreferrer'>Download Offer To Sell</a>,
            },
            {
                key: 'download-interest-calculation',
                label: <a href={`${process.env.API_HOST}/loan-drawdowns/${drawdown.uuid}/download-interest-calculation`} target='_blank' rel='noreferrer'>Download Interest Calculation</a>,
            },
        ],
    };

    // For FWT1, display a dropdown button with options to download offer to sell and interest calculation
    const disbursalInstructedButton: ReactElement = loanHasFwt1Warehouse && drawdown.status === LoanDrawdownStatusEnum.Approved ? (
        <Dropdown.Button
            onClick={onClickInstructDisbursal}
            menu={disbursalInstructedMenu}
            type='primary'
        >
            Disbursal Instructed
        </Dropdown.Button>
    ) : drawdown.status === LoanDrawdownStatusEnum.Approved && (
        <Button
            onClick={onClickInstructDisbursal}
            type='primary'
        >
            Disbursal Instructed
        </Button>
    );

    const downloadSummaryBlock: ReactElement = ([
        LoanDrawdownStatusEnum.PendingApproval,
        LoanDrawdownStatusEnum.Approved,
        LoanDrawdownStatusEnum.DisbursalInstructed,
    ].includes(drawdown.status) || (drawdown.status === LoanDrawdownStatusEnum.Processed && drawdown.loanFileUuid)) && (
        <Button
            href={`${process.env.API_HOST}/loan-drawdowns/${drawdown.uuid}/download-summary`}
            target='_blank'
        >
            Download Summary
        </Button>
    );

    const disbursalCalculationModalBlock: ReactElement = ModalStateEnum.Off !== isDisbursalCalculationModalOpen && (
        <DrawdownDisbursalCalculationModal
            loanUuid={loanUuid}
            loanDrawdownUuid={loanDrawdownUuid}
            isOpen={ModalStateEnum.Open === isDisbursalCalculationModalOpen}
            onCancel={onCloseDisbursalCalculationModal}
        />
    );

    const disbursalInstructedModalBlock: JSX.Element = ModalStateEnum.Off !== isDisbursalInstructedModalOpen && (
        <DrawdownDisbursalInstructedModal
            loanDrawdownUuid={loanDrawdownUuid}
            isOpen={ModalStateEnum.Open === isDisbursalInstructedModalOpen}
            onCancel={onCloseDisbursalInstructedModal}
        />
    );

    const qsInstructedModalBlock: JSX.Element = ModalStateEnum.Off !== isQsInstructedModalOpen && (
        <DrawdownQsInstructedModal
            loanDrawdownUuid={loanDrawdownUuid}
            isOpen={ModalStateEnum.Open === isQsInstructedModalOpen}
            onCancel={onCloseQsInstructedModal}
        />
    );

    const qsReceivedModalBlock: JSX.Element = ModalStateEnum.Off !== isQsReceivedModalOpen && (
        <DrawdownQsReceivedModal
            loanDrawdownUuid={loanDrawdownUuid}
            isOpen={ModalStateEnum.Open === isQsReceivedModalOpen}
            onCancel={onCloseQsReceivedModal}
        />
    );

    const processModalBlock: ReactElement = ModalStateEnum.Off !== isProcessModalOpen && (
        <DrawdownProcessModal
            loanDrawdownUuid={loanDrawdownUuid}
            isOpen={ModalStateEnum.Open === isProcessModalOpen}
            onCancel={onCloseProcessModal}
        />
    );

    const drawdownAmountBlock: string = drawdown.approvedDrawdownAmount ? currencyFormatter.format(drawdown.approvedDrawdownAmount) : '-';

    const retainedFundsBalance: number = loan.retainedFunds - (drawdown.retainedFundsAmount ?? 0);
    const retainedFundsBalanceBlock: ReactElement = validateAndDisplayBalanceStatuses.includes(drawdown.status) && (
        <span className='remaining-balance'>({currencyFormatter.format(retainedFundsBalance)} Remaining)</span>
    );
    const retainedFundsAmountBlock: string = drawdown.retainedFundsAmount !== null ? currencyFormatter.format(drawdown.retainedFundsAmount) : '-';

    const retainedContingencyBalance: number = loan.retainedContingency - (drawdown.retainedContingencyAmount ?? 0);
    const retainedContingencyBalanceBlock: ReactElement = validateAndDisplayBalanceStatuses.includes(drawdown.status) && (
        <span className='remaining-balance'>({currencyFormatter.format(retainedContingencyBalance)} Remaining)</span>
    );
    const retainedContingencyAmountBlock: string = drawdown.retainedContingencyAmount !== null ? currencyFormatter.format(drawdown.retainedContingencyAmount) : '-';

    const itemsBlock: ReactElement[] = _.map(_.sortBy(items, [ 'sortOrder' ]), (item: ILoanDrawdownItem) => {
        const itemErrors: IItemErrors = { };
        if (validateAndDisplayBalanceStatuses.includes(drawdown.status)) {
            if (!item.description) {
                itemErrors.description = 'Please enter a description';
            }
            if (item.amount === null) {
                itemErrors.amount = 'Please enter an amount';
            }
        }

        const itemLabelBlock: ReactElement = (
            <PopoverWarning
                help={itemErrors.description}
                validateStatus={itemErrors.description && 'error'}
            >
                {item.description ? item.description : '-'}
            </PopoverWarning>
        );

        const itemAmountBlock: ReactElement = (
            <PopoverWarning
                help={itemErrors.amount}
                validateStatus={itemErrors.amount && 'error'}
            >
                {item.amount !== null ? currencyFormatter.format(item.amount) : '-'}
            </PopoverWarning>
        );

        return <Descriptions.Item className='fee' label={itemLabelBlock}>{itemAmountBlock}</Descriptions.Item>;
    });

    const totalToBeDisbursed: number = getLoanDrawdownTotalToBeDisbursed(drawdown, items);

    const disbursalTotal: number = (drawdown.retainedContingencyAmount ?? 0) + (drawdown.retainedFundsAmount ?? 0);
    const tableErrors: ITableErrors = {};

    if (validateAndDisplayBalanceStatuses.includes(drawdown.status)) {
        if (drawdown.approvedDrawdownAmount === null) {
            tableErrors.approvedDrawdownAmount = 'Please enter an amount';
        }

        if (_.round(disbursalTotal, 2) !== _.round(totalToBeDisbursed, 2)) {
            tableErrors.totalToBeDisbursed = `Disbursal Total does not satisfy the Total To Be Disbursed of ${currencyFormatter.format(totalToBeDisbursed)}`;
        }

        if (drawdown.retainedFundsAmount > loan.retainedFunds) {
            tableErrors.retainedFundsAmount = 'The amount exceeds the available funds';
        }
        if (drawdown.retainedContingencyAmount > loan.retainedContingency) {
            tableErrors.retainedContingencyAmount = 'The amount exceeds the available funds';
        }
    }

    const disbursalTableBlock: ReactElement = [
        LoanDrawdownStatusEnum.QsReceived,
        LoanDrawdownStatusEnum.PendingApproval,
        LoanDrawdownStatusEnum.Approved,
        LoanDrawdownStatusEnum.DisbursalInstructed,
        LoanDrawdownStatusEnum.Processed,
    ].includes(drawdown.status) && (
        <>
            {drawdown.status === LoanDrawdownStatusEnum.QsReceived && <Button className='edit-disbursal' onClick={onClickDisbursalCalculation}>Edit</Button>}
            <Typography.Title className='disbursal-title' level={4}>Disbursal</Typography.Title>
            <Descriptions bordered={true} column={1}>
                <Descriptions.Item label='Approved Drawdown Amount'>
                    <PopoverWarning
                        help={tableErrors.approvedDrawdownAmount}
                        validateStatus={tableErrors.approvedDrawdownAmount && 'error'}
                    >
                        {drawdownAmountBlock}
                    </PopoverWarning>
                </Descriptions.Item>
                <Descriptions.Item span={2} className='fees-title' label='Fees'>&nbsp;</Descriptions.Item>
                {itemsBlock}
                <Descriptions.Item className='disbursal-total' label='Total to be Disbursed'>
                    <PopoverWarning
                        help={tableErrors.totalToBeDisbursed}
                        validateStatus={tableErrors.totalToBeDisbursed && 'error'}
                    >
                        {currencyFormatter.format(totalToBeDisbursed)}
                    </PopoverWarning>
                </Descriptions.Item>
                <Descriptions.Item span={2} className='disbursed-from-title' label='Disbursed From'>&nbsp;</Descriptions.Item>
                <Descriptions.Item className='retained-funds' label='Retained Funds'>
                    <PopoverWarning
                        help={tableErrors.retainedFundsAmount}
                        validateStatus={tableErrors.retainedFundsAmount && 'error'}
                    >
                        {retainedFundsAmountBlock}
                        {retainedFundsBalanceBlock}
                    </PopoverWarning>
                </Descriptions.Item>
                <Descriptions.Item className='retained-contingency' label='Retained Contingency'>
                    <PopoverWarning
                        help={tableErrors.retainedContingencyAmount}
                        validateStatus={tableErrors.retainedContingencyAmount && 'error'}
                    >
                        {retainedContingencyAmountBlock}
                        {retainedContingencyBalanceBlock}
                    </PopoverWarning>
                </Descriptions.Item>
            </Descriptions>
        </>
    );

    return (
        <Layout uuid={loanUuid} section='drawdown'>
            <Space className='actions'>
                {drawdown.status === LoanDrawdownStatusEnum.Requested && <Button type='primary' onClick={onClickQsInstructed}>QS Instructed</Button>}
                {drawdown.status === LoanDrawdownStatusEnum.QsInstructed && <Button type='primary' onClick={onClickQsReceived}>QS Received</Button>}
                {downloadSummaryBlock}
                {drawdown.status === LoanDrawdownStatusEnum.QsReceived && <Button type='primary' onClick={onClickSubmitForApproval}>Submit for Approval</Button>}
                {approveActionBlock}
                {disbursalInstructedButton}
                {processActionBlock}
            </Space>
            <Space>
                <Typography.Title className='drawdown-title' level={2}>Drawdown {drawdown.drawdownNumber}</Typography.Title>
                <LoanDrawdownStatusTag loanDrawdown={drawdown} />
            </Space>
            <Descriptions layout='vertical' bordered={true}>
                <Descriptions.Item label='Date Requested'>{drawdown.requestedTime ? dayjs(drawdown.requestedTime).format('Do MMMM YYYY hh:mma') : '-'}</Descriptions.Item>
                <Descriptions.Item label='Amount Requested'>{drawdown.requestedAmount ? currencyFormatter.format(drawdown.requestedAmount) : '-'}</Descriptions.Item>
                <Descriptions.Item label='QS Certified Amount'>{drawdown.qsCertifiedAmount ? currencyFormatter.format(drawdown.qsCertifiedAmount) : '-'}</Descriptions.Item>
            </Descriptions>
            {disbursalTableBlock}
            {disbursalInstructedModalBlock}
            {qsInstructedModalBlock}
            {qsReceivedModalBlock}
            {disbursalCalculationModalBlock}
            {processModalBlock}
        </Layout>
    );
}